import { Box, Text } from '@bpba/ui-components';
import { useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { TableColumn } from 'react-data-table-component';
import { getInformesAnteriores } from '../../rest/queries/getInformesAnteriores';
import { InformesAnterioresResponse } from '../../rest/Queries';
import { InformesAnterioresModel } from '../../rest/models/informesAnterioresModel';
import { toSentenceCase } from '../../utils/text';
import { FiltersCard } from './FiltersCard';
import { TableCard } from './TableCard';
import { Paginator } from './Paginator';
import { DEFAULT_FILTERS, FadeSwap, FiltersForm, IdLabel, monthNames, PAGE_SIZE, ResponsiveGapBox } from './styled';

// Labels conocidos por id de tipo. Si el backend devuelve un id nuevo, cae al
// fallback `Tipo N` para no romper el select y la columna.
const INFORME_LABELS: Record<string, string> = {
  '1': 'Semana económica',
  '3': 'Índice Banco Provincia',
  '4': 'Documentos de Trabajo',
};
const labelForType = (id: string) => INFORME_LABELS[id] ?? `Tipo ${id}`;

export const InformesAnteriores = () => {
  const methods = useForm<FiltersForm>({ defaultValues: DEFAULT_FILTERS });
  const [informe, year, month] = useWatch({
    control: methods.control,
    name: ['informe', 'year', 'month'],
  });
  const [page, setPage] = useState(1);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Un único GET sin filtros al mount. El dataset es chico (~134 registros),
  // así que filtramos client-side y evitamos round-trips al backend.
  // Para volver a filtrar server-side: descomentar `variables` de abajo y
  // reactivar la rama de filtrado en `filteredData` (ver más abajo).
  const { data: { getInformes } = {}, loading } = useQuery<InformesAnterioresResponse>(getInformesAnteriores, {
    // variables: {
    //   type: informe || undefined,
    //   year: year || undefined,
    //   month: month || undefined,
    // },
    onCompleted() {
      setErrorMessage(null);
    },
    onError(e) {
      const status = (e as any)?.networkError?.statusCode;
      if (status && status >= 400) {
        setErrorMessage('Ocurrió un error en la consulta, por favor intentelo más tarde');
      } else {
        setErrorMessage('No se pudo cargar el listado. Verificá tu conexión e intentá nuevamente.');
      }
      // eslint-disable-next-line no-console
      console.error('[InformesAnteriores] query error', e);
    },
  });

  // Filtrado client-side sobre el universo completo. Si se vuelve a server-side
  // (ver `variables` comentadas arriba), reemplazar por `getInformes ?? []`.
  const filteredData = useMemo(() => {
    if (!getInformes) return [];
    return getInformes.filter((r) => {
      if (informe && String(r.type) !== informe) return false;
      const [y, m] = r.startDate.split('-');
      if (year && y !== year) return false;
      if (month && m !== month) return false;
      return true;
    });
  }, [getInformes, informe, year, month]);

  const totalRows = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedData = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredData.slice(start, start + PAGE_SIZE);
  }, [filteredData, currentPage]);

  // Catálogo de tipo/año/mes derivado del universo completo (el request nunca
  // se filtra en servidor, así que `getInformes` siempre trae todo).
  const [filterCatalog, setFilterCatalog] = useState<{
    types: string[];
    years: string[];
    months: string[];
  } | null>(null);
  useEffect(() => {
    if (!filterCatalog && getInformes && getInformes.length > 0) {
      const types = Array.from(new Set(getInformes.map((r) => String(r.type))))
        .filter(Boolean)
        .sort((a, b) => Number(a) - Number(b));
      const years = Array.from(new Set(getInformes.map((r) => r.startDate.split('-')[0])))
        .filter(Boolean)
        .sort((a, b) => Number(b) - Number(a));
      const months = Array.from(new Set(getInformes.map((r) => r.startDate.split('-')[1])))
        .filter(Boolean)
        .sort((a, b) => Number(a) - Number(b));
      setFilterCatalog({ types, years, months });
    }
  }, [filterCatalog, getInformes]);

  useEffect(() => {
    setPage(1);
  }, [informe, year, month]);

  const informeOptions = useMemo<IdLabel[]>(
    () => [{ id: '', label: 'Todos los informes' }, ...(filterCatalog?.types ?? []).map((t) => ({ id: t, label: labelForType(t) }))],
    [filterCatalog]
  );
  const yearOptions = useMemo<IdLabel[]>(
    () => [{ id: '', label: 'Todos los años' }, ...(filterCatalog?.years ?? []).map((y) => ({ id: y, label: y }))],
    [filterCatalog]
  );
  const monthOptions = useMemo<IdLabel[]>(
    () => [{ id: '', label: 'Todos los meses' }, ...(filterCatalog?.months ?? []).map((m) => ({ id: m, label: monthNames[m] ?? m }))],
    [filterCatalog]
  );

  const columns = useMemo<TableColumn<InformesAnterioresModel>[]>(
    () => [
      {
        name: 'Título',
        selector: (row) => toSentenceCase(row.title) + ' - ' + row.period,
        minWidth: '300px',
        grow: 3,
        wrap: true,
        cell: (row) => {
          const fullText = toSentenceCase(row.title) + ' - ' + row.period;
          return (
            // data-tag="allowRowEvents" es el flag de react-data-table-component
            // para que el click propague a `onRowClicked` (por defecto sólo
            // dispara sobre celdas sin renderer custom).
            <div
              data-tag="allowRowEvents"
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                minHeight: 'calc(2 * 1.5em)',
                lineHeight: 1.5,
              }}
            >
              <span
                data-tag="allowRowEvents"
                title={fullText}
                style={{
                  color: 'inherit',
                  wordBreak: 'break-word',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {fullText}
              </span>
            </div>
          );
        },
      },
      {
        name: 'Informe',
        selector: (row) => labelForType(String(row.type)),
        width: '170px',
        cell: (row) => {
          const label = labelForType(String(row.type));
          return (
            <span
              data-tag="allowRowEvents"
              title={label}
              style={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%',
              }}
            >
              {label}
            </span>
          );
        },
      },
      {
        name: 'Año',
        selector: (row) => row.startDate.split('-')[0],
        width: '55px',
      },
      {
        name: 'Mes',
        selector: (row) => monthNames[row.startDate.split('-')[1]] ?? row.startDate.split('-')[1],
        width: '110px',
      },
    ],
    []
  );

  const handlePrev = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);
  const handleNext = useCallback(() => setPage((p) => p + 1), []);
  const handleGoTo = useCallback((p: number) => setPage(p), []);
  const handleRowClick = useCallback((row: InformesAnterioresModel) => {
    if (!row?.fileKey) return;
    // Click sintético en un <a>: más robusto contra popup blockers que
    // `window.open`, y trata la apertura como navegación iniciada por el user.
    const a = document.createElement('a');
    a.href = `/gateway/cdn/${encodeURIComponent(row.fileKey)}`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  return (
    <>
      <FadeSwap>
        <Text
          as="h2"
          fontSize={['18px', '18px', '18px', '20px', '22px']}
          fontWeight={500}
          color={'primary.0'}
          mt={'16px'}
          mb={'24px'}
          style={{ lineHeight: 1.4 }}
        >
          Informes anteriores
        </Text>
      </FadeSwap>
      <FadeSwap>
        <Box flexDirection="column" style={{ gap: '16px' }}>
          <ResponsiveGapBox flexDirection={['column', null, 'column', 'column', 'row']} alignItems="flex-start">
            <FiltersCard methods={methods} informeOptions={informeOptions} yearOptions={yearOptions} monthOptions={monthOptions} />
            <TableCard loading={loading} data={pagedData} columns={columns} errorMessage={errorMessage} onRowClick={handleRowClick} />
          </ResponsiveGapBox>
          <Paginator
            loading={loading}
            currentPage={currentPage}
            totalPages={totalPages}
            onPrev={handlePrev}
            onNext={handleNext}
            onGoTo={handleGoTo}
          />
        </Box>
      </FadeSwap>
    </>
  );
};
